<template>
  <page-container
    ref="pageContainerRef"
    class="hotelux-home-page"
    :class="{ 'header-sticky': isHeaderSticky }"
    :show-navbar="false"
    :show-header="false"
  >
    <hotelux-header
      class="header"
      :logo-url="logoUrl"
      :show-currency="true"
      :is-sticky="isHeaderSticky"
    >
      <template #center>
        <search-bar
          v-if="isHeaderSticky"
          class="header-search"
          :is-sticky="true"
          @search-click="handleSearch"
          @search-change="handleSearchChange"
        />
      </template>
    </hotelux-header>

    <main class="hotelux-home">
      <section class="hero">
        <el-carousel
          class="hero-carousel"
          :autoplay="heroBanners.length > 1"
          :interval="4000"
          :arrow="heroBanners.length > 1 ? 'hover' : 'never'"
          indicator-position="none"
        >
          <el-carousel-item
            v-for="banner in heroBanners"
            :key="banner._id || banner.img"
          >
            <button
              type="button"
              class="hero-banner"
              :class="{ clickable: isClickable(banner) }"
              :style="{ backgroundImage: `url(${heroImage(banner)})` }"
              @click="handleEntryClick(banner)"
            >
              <img
                v-if="banner.extendedProp?.partnerLogo"
                class="hero-partner-logo"
                :src="banner.extendedProp.partnerLogo"
                alt=""
              />
              <span v-if="heroTitle(banner)" class="hero-title">
                {{ heroTitle(banner) }}
              </span>
              <span v-if="heroSubtitle(banner)" class="hero-subtitle">
                {{ heroSubtitle(banner) }}
              </span>
              <span v-if="heroAction(banner)" class="hero-action">
                {{ heroAction(banner) }}
              </span>
            </button>
          </el-carousel-item>
        </el-carousel>
        <div v-if="!isHeaderSticky" class="hero-indicators">
          <span
            v-for="(banner, index) in heroBanners"
            :key="index"
            class="indicator-dot"
            :class="{ active: index === 2 }"
          />
        </div>
        <search-bar
          v-if="!isHeaderSticky"
          class="hero-search"
          @search-click="handleSearch"
          @search-change="handleSearchChange"
        />
      </section>

      <section
        v-if="homeStore.membershipMatch"
        class="home-section membership-section"
      >
        <div class="membership-header">
          <span class="membership-label">MEMBERSHIP MATCH</span>
          <h2>{{ getI18nText(homeStore.sectionTitles.membershipMatch) }}</h2>
          <p class="membership-desc">
            {{ getI18nText(homeStore.membershipMatchDesc) }}
          </p>
        </div>
        <div class="membership-cards">
          <button
            v-for="card in homeStore.membershipMatch"
            :key="card._id"
            type="button"
            class="membership-card"
            @click="handleEntryClick(card)"
          >
            <h3>{{ getI18nText(card.titleI18n) }}</h3>
            <p>{{ getI18nText(card.subtitleI18n) }}</p>
            <span class="membership-action">
              {{ getI18nText(card.extendedProp?.actionLabelI18n) }}
            </span>
          </button>
        </div>
      </section>

      <section
        v-if="homeStore.exclusiveOffers.length"
        class="home-section exclusive-section"
      >
        <h2>{{ getI18nText(homeStore.sectionTitles.exclusiveOffers) }}</h2>
        <div class="offer-list">
          <button
            v-for="offer in homeStore.exclusiveOffers"
            :key="offer._id"
            type="button"
            class="offer-card"
            @click="handleEntryClick(offer)"
          >
            <img :src="desktopImage(offer)" alt="" />
            <span v-if="getI18nText(offer.badgeI18n)" class="offer-badge">
              {{ getI18nText(offer.badgeI18n) }}
            </span>
            <span class="offer-content">
              <strong>{{ getI18nText(offer.titleI18n) }}</strong>
              <span v-if="getI18nText(offer.extendedProp?.timeI18n)">
                {{ getI18nText(offer.extendedProp?.timeI18n) }}
              </span>
              <span v-if="offer.extendedProp?.price" class="offer-price">
                {{ offer.extendedProp.price }}
              </span>
            </span>
          </button>
        </div>
      </section>

      <section class="home-section curated-section">
        <h2>{{ getI18nText(homeStore.sectionTitles.curatedCollections) }}</h2>
        <div v-loading="hotelsLoading" class="hotel-list">
          <hotel-card
            v-for="hotel in hotels"
            :key="hotel._id"
            :hotel="hotel"
            @select="handleSelectHotel(hotel)"
          />
        </div>
      </section>

      <section v-if="homeStore.destinations.length" class="destination-links">
        <div
          v-for="destination in homeStore.destinations"
          :key="getI18nText(destination.nameI18n)"
          class="destination-group"
        >
          <h3>{{ getI18nText(destination.nameI18n) }}</h3>
          <button
            v-for="hotel in destination.hotels"
            :key="hotel._id"
            type="button"
            @click="navigateToHotel(hotel)"
          >
            {{ getI18nText(hotel.nameI18n) }}
          </button>
        </div>
      </section>
    </main>
  </page-container>
</template>

<script setup>
import HotelCard from "@packages/booking/hotel/HotelCard/V2.vue";
import { storeToRefs } from "pinia";
import { computed, nextTick, onMounted, onUnmounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";

import { useHotelStoreHook } from "@/distributions/consumer/y/store/hotel";
import { useHoteluxHomeStoreHook } from "@/distributions/consumer/y/store/hoteluxHome";
import { recordLocationHistory } from "@/distributions/consumer/y/utils/historyHelper";
import { syncHotelDetailLocation } from "@/distributions/consumer/y/utils/hotelNavigation";
import { useHotels } from "@/hooks/useHotels";
import { getI18nText } from "@/plugins/i18n";
import { useUserStoreHook } from "@/store/modules/user";
import { paramsToQuery } from "@/utils/tool";

import { useYChannelStoreHook } from "../../../../store/channel";
import HoteluxHeader from "../../components/Header/index.vue";
import SearchBar from "../../components/SearchBar/index.vue";

const router = useRouter();
const { t } = useI18n();
const channelStore = useYChannelStoreHook();
const hotelStore = useHotelStoreHook();
const homeStore = useHoteluxHomeStoreHook();
const userStore = useUserStoreHook();
const { searchModel } = storeToRefs(hotelStore);
const { language, preferred } = storeToRefs(userStore);
const DEFAULT_HERO = {
  img: "https://uploads.ytripos.com/uploaded/lkTce8_RRZXTg2mvkzB-I1uPdazV/y-web-index-bg2.png"
};

const logoUrl = computed(
  () => channelStore.configs?.pages?.Index?.logoUrl || ""
);
const configuredBanners = computed(() => {
  const list = channelStore.configs?.pages?.Index?.banners?.list || [];
  return list.filter(item => item.show?.({ user: userStore.user }) ?? true);
});
const heroBanners = computed(() => {
  const entryBanners = homeStore.banners.filter(
    item =>
      item.imageUrl ||
      getI18nText(item.imageUrlI18n) ||
      item.imageAlternative?.imageUrl
  );
  const banners = entryBanners.length ? entryBanners : configuredBanners.value;
  return banners.length ? banners : [DEFAULT_HERO];
});
const curatedFilter = computed(() => ({
  hotels: homeStore.curatedHotelIds.map(_id => ({ _id }))
}));

const {
  hotels,
  loading: hotelsLoading,
  fetchHotels
} = useHotels({
  pageSize: 6,
  stay: searchModel.value.stay,
  filter: curatedFilter,
  preferred
});

const desktopImage = item =>
  item?.imageAlternative?.imageUrl ||
  getI18nText(item?.imageUrlI18n) ||
  item?.imageUrl ||
  "";
const heroImage = item =>
  item?.imageAlternative?.imageUrl ||
  getI18nText(item?.imageUrlI18n) ||
  item?.imageUrl ||
  item?.img ||
  "";
const heroTitle = item =>
  item?._id ? getI18nText(item.titleI18n) : item?.title ? t(item.title) : "";
const heroSubtitle = item =>
  item?._id
    ? getI18nText(item.subtitleI18n)
    : item?.subtitle
    ? t(item.subtitle)
    : item?.content
    ? t(item.content)
    : "";
const heroAction = item =>
  item?._id
    ? getI18nText(item.extendedProp?.actionLabelI18n)
    : item?.btn
    ? t(item.btn)
    : "";
const entryLink = item =>
  item?.linkAlternative?.path ? item.linkAlternative : item?.link;
const isClickable = item =>
  Boolean(item?.onClick || entryLink(item)?.path || item?.target?.ref);

const handleEntryClick = item => {
  if (item?.onClick) {
    item.onClick({ router, userStore });
    return;
  }

  const target = item?.target?.ref;
  const targetRef = target?._id || target;
  if (targetRef) {
    navigateToHotel(typeof target === "object" ? target : { _id: targetRef });
    return;
  }

  const link = entryLink(item);
  if (!link?.path) return;
  if (["webpage", "browser", "externalApp"].includes(link.type)) {
    const target = link.extendedProp?.target === "_self" ? "_self" : "_blank";
    if (target === "_self") {
      window.location.assign(link.path);
      return;
    }
    window.open(link.path, target, "noopener,noreferrer");
    return;
  }
  router.push(link.path);
};

const ROUTER_PATH_MAP = {
  moduleTpHotel: "/hotel/detail",
  city: "/hotel/list",
  googleMapPolitical: "/hotel/list",
  search: "/hotel/list"
};
const handleSearchChange = () => {
  const query = paramsToQuery();
  if (query?.locationType === "moduleTpHotel") handleSearch();
};
const handleSearch = () => {
  recordLocationHistory(hotelStore.hotelSearchModel.location);
  const query = paramsToQuery();
  router.push({
    path: ROUTER_PATH_MAP[query.locationType] || "/hotel/list",
    query
  });
};

const navigateToHotel = hotel => {
  hotelStore.SET_SELECTED_HOTEL(hotel);
  const locationQuery = syncHotelDetailLocation(
    hotelStore,
    hotel,
    language.value
  );
  router.push({
    path: "/hotel/detail",
    query: {
      ...locationQuery,
      checkIn: hotelStore.searchModel.stay.date.checkIn,
      checkOut: hotelStore.searchModel.stay.date.checkOut,
      numberOfAdults: hotelStore.searchModel.stay.guest.numberOfAdults,
      numberOfChildren: hotelStore.searchModel.stay.guest.numberOfChildren,
      numberOfRooms: hotelStore.searchModel.stay.guest.numberOfRooms,
      hotelId: hotel._id
    }
  });
};
const handleSelectHotel = hotel => navigateToHotel(hotel);

const isHeaderSticky = ref(false);
const pageContainerRef = ref(null);

const handleScroll = event => {
  const target = event?.target || pageContainerRef.value?.$refs?.pageRef;
  if (!target) return;

  const scrollTop = target.scrollTop || 0;
  isHeaderSticky.value = scrollTop > 50;
};

onMounted(async () => {
  await homeStore.fetchHome();
  await fetchHotels();
  await nextTick();

  setTimeout(() => {
    const pageElement = pageContainerRef.value?.$refs?.pageRef;
    if (pageElement) {
      pageElement.addEventListener("scroll", handleScroll, { passive: true });
      handleScroll({ target: pageElement });
    }
  }, 100);
});

onUnmounted(() => {
  const pageElement = pageContainerRef.value?.$refs?.pageRef;
  if (pageElement) {
    pageElement.removeEventListener("scroll", handleScroll);
  }
});

defineOptions({ name: "HoteluxHome" });
</script>

<style lang="scss" scoped>
.hotelux-home-page {
  :deep(.wrapper) {
    min-width: 1200px;
    min-height: calc(100vh + 500px);
    padding-top: 0 !important;
  }

  :deep(.header) {
    --y-currency-selector-color: #fff;

    position: absolute;
    top: 0;
    left: 0;
    z-index: 10;
    width: 100%;
    height: 104px;
    padding: 28px max(60px, calc((100% - 1520px) / 2));
    color: #fff;
    background: transparent;
    transition: all 0.3s ease;
  }

  :deep(.header .y-current-user) {
    --y-text-color: #fff;
  }

  &.header-sticky {
    :deep(.header) {
      position: fixed !important;
      height: auto !important;
      padding: 16px max(60px, calc((100% - 1520px) / 2)) !important;
      color: #212223 !important;
      background: #f8f8f8 !important;
      box-shadow: 0 2px 8px rgb(0 0 0 / 8%) !important;

      --y-currency-selector-color: #212223;
    }

    :deep(.header .y-current-user) {
      --y-text-color: #212223;
    }
  }
}

.header-search {
  flex: 1;
  padding: 0 24px;

  :deep(.hotelux-search-bar) {
    padding: 0;
  }
}

.hotelux-home {
  position: relative;
  width: 100%;
  min-height: 100vh;
  color: #212223;
  background: #fff;
  font-family: Lato, -apple-system, sans-serif;
}

.hero {
  position: relative;
  width: 100%;
  height: 600px;
  margin-bottom: 80px;
}

.hero-carousel,
:deep(.hero-carousel .el-carousel__container) {
  height: 100%;
}

.hero-banner {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  padding: 140px 60px 120px;
  color: #fff;
  background-position: center;
  background-size: cover;
  border: 0;

  &::after {
    position: absolute;
    inset: 0;
    content: "";
    background: rgb(0 0 0 / 20%);
  }

  &.clickable {
    cursor: pointer;
  }

  > * {
    position: relative;
    z-index: 1;
  }
}

.hero-title {
  max-width: 1200px;
  font-family: Cormorant, var(--y-title-font), serif;
  font-size: 48px;
  font-weight: 700;
  line-height: 1.2;
  text-align: center;
  letter-spacing: 0;
}

.hero-partner-logo {
  max-width: 180px;
  max-height: 72px;
  margin-bottom: 24px;
  object-fit: contain;
}

.hero-subtitle {
  max-width: 1000px;
  margin-top: 16px;
  font-family: Barlow, -apple-system, sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 1.5;
  color: #d8d8d8;
  text-align: center;
}

.hero-action {
  min-width: 140px;
  padding: 11px 20px;
  margin-top: 32px;
  font-size: 10px;
  font-weight: 400;
  line-height: 1;
  color: #fff;
  letter-spacing: 0.5px;
  text-align: center;
  background: transparent;
  border: 1px solid #fff;
  transition: all 0.3s ease;

  &:hover {
    background: rgb(255 255 255 / 10%);
  }
}

.hero-indicators {
  position: absolute;
  bottom: 56px;
  left: 50%;
  z-index: 2;
  display: flex;
  gap: 9px;
  align-items: center;
  transform: translateX(-50%);
}

.indicator-dot {
  width: 8px;
  height: 6px;
  background: #bfbfbf;
  border-radius: 50%;
  transition: all 0.3s ease;

  &.active {
    width: 10px;
    height: 7px;
    background: #fff;
  }
}

.hero-search {
  --search-bar-inner-width: min(1224px, calc(100vw - 120px));

  position: absolute;
  right: 0;
  bottom: 24px;
  left: 0;
  z-index: 3;

  :deep(.search-bar-inner) {
    min-height: 56px;
    padding: 16px 24px;
    background: #fff;
    border: 0;
    box-shadow: 0 2px 8px rgb(0 0 0 / 8%);
  }
}

.home-section {
  width: min(1440px, calc(100% - 120px));
  padding: 80px 0 60px;
  margin: 0 auto;

  h2 {
    margin-bottom: 48px;
    font-family: Cormorant, var(--y-title-font), serif;
    font-size: 48px;
    font-weight: 700;
    line-height: 1.2;
    color: #212223;
    text-align: left;
    letter-spacing: 1px;
  }
}

.membership-section {
  padding: 100px 0 80px;
  background: #fff;
}

.membership-header {
  margin-bottom: 56px;
  text-align: left;

  .membership-label {
    display: inline-block;
    margin-bottom: 16px;
    font-size: 12px;
    font-weight: 700;
    line-height: 1.5;
    color: #757575;
    letter-spacing: 1px;
  }

  h2 {
    margin-bottom: 20px;
  }

  .membership-desc {
    max-width: 800px;
    font-size: 16px;
    line-height: 1.5;
    color: #757575;
  }
}

.membership-cards {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 32px;
}

.membership-card {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  min-height: 273px;
  padding: 40px 36px;
  text-align: left;
  cursor: pointer;
  background: #fff;
  border: 1px solid #e9e9e9;
  transition: all 0.3s ease;

  &:hover {
    box-shadow: 0 8px 24px rgb(0 0 0 / 8%);
    transform: translateY(-2px);
  }

  h3 {
    margin-bottom: 16px;
    font-family: Cormorant, var(--y-title-font), serif;
    font-size: 22px;
    font-weight: 700;
    color: #212223;
    letter-spacing: 0;
  }

  p {
    flex: 1;
    margin-bottom: 24px;
    font-size: 16px;
    line-height: 1.25;
    color: #757575;
  }

  .membership-action {
    font-size: 16px;
    font-weight: 700;
    line-height: 1.125;
    color: #212223;
    letter-spacing: 1px;
  }
}

.exclusive-section {
  padding: 80px 0;
  background: #fff;
}

.offer-list {
  display: flex;
  gap: 32px;
  overflow-x: auto;
}

.offer-card {
  position: relative;
  flex: 0 0 384px;
  min-height: 291px;
  padding: 0;
  overflow: hidden;
  color: #fff;
  text-align: left;
  cursor: pointer;
  background: #f5f5f5;
  border: 0;
  box-shadow: 0 0 0 rgb(0 0 0 / 10%);
  transition: all 0.3s ease;

  &:hover {
    box-shadow: 0 8px 24px rgb(0 0 0 / 10%);
  }

  &::after {
    position: absolute;
    inset: 40% 0 0;
    content: "";
    background: linear-gradient(180deg, transparent, rgb(0 0 0 / 70%));
  }

  img {
    width: 100%;
    height: 100%;
    min-height: 291px;
    object-fit: cover;
    opacity: 1;
    transition: transform 0.3s ease;
  }

  &:hover img {
    transform: scale(1.05);
  }
}

.offer-badge {
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 1;
  padding: 6px 10px;
  font-size: 10px;
  font-weight: 700;
  line-height: 1.5;
  letter-spacing: 0.5px;
  text-align: right;
  background: rgb(0 0 0 / 1%);
}

.offer-content {
  position: absolute;
  right: 24px;
  bottom: 24px;
  left: 24px;
  z-index: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;

  strong {
    font-family: Cormorant, var(--y-title-font), serif;
    font-size: 22px;
    font-weight: 700;
    line-height: 1.2;
    letter-spacing: 0;
  }

  span {
    font-size: 16px;
    line-height: 1.25;
    color: #fff;
    letter-spacing: 0;
  }
}

.offer-price {
  font-size: 16px;
  line-height: 1.25;
  color: #757575;
  letter-spacing: 0;
}

.curated-section {
  padding: 80px 0 120px;
  // background: #f5f5f5;
}

.hotel-list {
  display: flex;
  gap: 32px;
  min-height: 420px;
  overflow-x: auto;

  :deep(.hotel-card) {
    flex: 0 0 384px;
  }
}

.offer-list,
.hotel-list {
  padding-bottom: 16px;
  scrollbar-color: #212223 #e4e4e4;
  scrollbar-width: thin;
}

.destination-links {
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: 48px 40px;
  width: 100%;
  padding: 80px max(60px, calc((100% - 1520px) / 2)) 90px;
  color: rgb(255 255 255 / 90%);
  background: #212223;
}

.destination-group {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;

  h3 {
    margin-bottom: 8px;
    font-family: Lato, -apple-system, sans-serif;
    font-size: 16px;
    font-weight: 700;
    line-height: 1.125;
    color: #fff;
    letter-spacing: 1px;
  }

  button {
    padding: 0;
    font-size: 12px;
    line-height: 2;
    color: inherit;
    text-align: left;
    cursor: pointer;
    background: transparent;
    border: 0;
    transition: color 0.2s ease;

    &:hover {
      color: #fff;
    }
  }
}

@media screen and (width <= 1440px) {
  .home-section {
    width: calc(100% - 120px);
  }
}
</style>
